import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const MOBILE_QUERY = '(max-width: 900px)';
const COLLAPSED_STORAGE_KEY = 'helloant.sidebarCollapsed';

interface MobileDrawerContextValue {
  isMobile: boolean;
  /** Mobile drawer open state (no effect on desktop). */
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  /** Desktop sidebar collapsed state (no effect on mobile). Persisted. */
  desktopCollapsed: boolean;
  toggleDesktopCollapsed: () => void;
}

const MobileDrawerContext = createContext<MobileDrawerContextValue | null>(null);

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function MobileDrawerProvider({ children }: { children: ReactNode }) {
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY).matches : false,
  );
  const [isOpen, setIsOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState<boolean>(() => readStoredCollapsed());

  // Track viewport breakpoint via matchMedia (cheaper than resize, matches CSS).
  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY);
    const handler = (e: MediaQueryListEvent) => {
      setIsMobile(e.matches);
      // Crossing into desktop closes the drawer so state doesn't linger.
      if (!e.matches) setIsOpen(false);
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Body scroll lock while drawer is open on mobile.
  useEffect(() => {
    if (!isMobile || !isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobile, isOpen]);

  // Escape key closes the drawer.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen]);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((v) => !v), []);

  const toggleDesktopCollapsed = useCallback(() => {
    setDesktopCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const value = useMemo<MobileDrawerContextValue>(
    () => ({
      isMobile,
      isOpen,
      open,
      close,
      toggle,
      desktopCollapsed,
      toggleDesktopCollapsed,
    }),
    [isMobile, isOpen, open, close, toggle, desktopCollapsed, toggleDesktopCollapsed],
  );

  return <MobileDrawerContext.Provider value={value}>{children}</MobileDrawerContext.Provider>;
}

export function useMobileDrawer(): MobileDrawerContextValue {
  const v = useContext(MobileDrawerContext);
  if (!v) throw new Error('useMobileDrawer must be used inside MobileDrawerProvider');
  return v;
}
